import React, { useEffect, useState } from "react";
import { language } from "../../system/types/project/language";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";
import { brainGame, brainGameEntry } from "../../system/types/brainGame/brainGame";
import { TextInput } from "../FormElements/TextInput";
import { useTranslations } from "../../hooks/useTranslations";
import { getRandomItemByLevel } from "../../system/helper/brainGameRandom";
import { dispatch } from "../../App";
import { actions } from "../../redux";
import { BrainGameApi } from "../../system/api/BrainGameApi";
import { setProjectId } from "../../redux/project/actions";

const GameInput = () => {

    const { t } = useTranslations();

    const [sessionToken, setSessionToken] = useState<string>('');
    const [sessionEmail, setSessionEmail] = useState<string>('');

    const [brainGameData, setBrainGameData] = useState<brainGame | undefined>();
    const [baseLanguage, setBaseLanguage] = useState<language>();
    const [selectedLanguage, setSelectedLanguage] = useState<language>();
    const [activeEntry, setActiveEntry] = useState<brainGameEntry>();
    const [questionLanguage, setQuestionLanguage] = useState<language>();
    const [answerLanguage, setAnswerLanguage] = useState<language>();

    const [answerInput, setAnswerInput] = useState<string>('');
    const [correctAnswer, setCorrectAnswer] = useState<string>('');
    const [checkSet, setCheckSet] = useState<string>('');

    useSelector((state: RootState) => {

        if (state.activeUser.activeUserToken !== sessionToken) {
            setSessionToken(state.activeUser.activeUserToken);
        }

        if (state.activeUser.activeUserEmail !== sessionEmail) {
            setSessionEmail(state.activeUser.activeUserEmail);
        }

        if (state.brainGame.gameData !== brainGameData) {
            setBrainGameData(state.brainGame.gameData);
        }
        if (state.brainGame.activeLanguage !== selectedLanguage) {
            setSelectedLanguage(state.brainGame.activeLanguage);
        }
        if (state.brainGame.activeEntry !== activeEntry) {
            setActiveEntry(state.brainGame.activeEntry);
        }
    });

    const getRandomEntry = () => {

        if (typeof brainGameData === 'undefined') {
            return;
        }

        if (brainGameData.keyEntries.length === 0) {
            return;
        }

        setBaseLanguage(brainGameData?.baseLanguage);
        setAnswerInput('');
        setCheckSet('');
        setCorrectAnswer('');

        const entry = getRandomItemByLevel(brainGameData.keyEntries);

        if (typeof entry !== 'undefined') {
            dispatch(actions.brainGame.setActiveEntry(entry));
            return;
        }

        dispatch(actions.brainGame.removeActiveEntry());
    };


    const randomQuestion = () => {
        const random = Math.random() * 10;
        const strRandom = random.toString();
        const check = parseInt(strRandom);

        if (check % 2) {
            setQuestionLanguage(selectedLanguage);
            setAnswerLanguage(baseLanguage);
            return;
        }
        setQuestionLanguage(baseLanguage);
        setAnswerLanguage(selectedLanguage);
    };

    const changeAnswerInput = (input: string) => {
        setAnswerInput(input);
    }

    const checkAnswer = (event: React.MouseEvent<HTMLButtonElement>) => {
        
        event.preventDefault();

        if (checkSet !== '') {
            getRandomEntry();
            return;
        }

        let answer = '';
        activeEntry?.baseModel.translations.forEach((entry) => {
            if (entry.isoCode === answerLanguage?.isoCode) {
                answer = entry.text;
            }
        });

        if (answer === answerInput) {
            setCheckSet('success');
            return;
        }

        setCheckSet('failed');
        setCorrectAnswer(answer);
    }

    const showQuestion = () => {

        let question = '';

        activeEntry?.baseModel.translations.forEach((entry) => {
            if (entry.isoCode === questionLanguage?.isoCode) {
                question = entry.text;
            }
        });

        question += ' (' + activeEntry?.baseModel.name + ')';

        return (
            <>
                <div className="row mb-3 align-items-center">
                    <div className="col-2">{questionLanguage?.name}:</div>
                    <div className="col-6">{question}</div>
                </div>
            </>
        )
    }

    const entryAnswer = () => {

        let disabled = false;
        let btnClass = 'btn btn-outline-primary';
        let btnText = t('braingame.checkAnswer', 'test');

        if (checkSet === 'success') {
            btnClass = 'btn btn-outline-success';
            btnText = t('braingame.nextquestion', 'weiter');
            disabled = true;
        }

        if (checkSet === 'failed') {
            btnClass = 'btn btn-outline-danger';
            btnText = t('braingame.nextquestion', 'weiter');
            disabled = true;
        }

        return (
            <>
                <div className="row mb-3 align-items-center">
                    <div className="col-2">{answerLanguage?.name}:</div>
                    <div className="col-6"><TextInput 
                        name="" 
                        value={answerInput} 
                        onChange={changeAnswerInput}
                        isDisabled={disabled}
                    ></TextInput></div>
                </div>
                <div className="row mb-3 align-items-center">
                    <div className="col-8">
                        <div className="row">
                            <div className="col"></div>
                            <div className="col-auto">
                                <button onClick={checkAnswer} className={btnClass}>{btnText}</button>
                            </div>
                        </div>
                    </div>
                </div>
                {
                    correctAnswer !== '' ? (
                        <div className="row mb-3 align-items-center">
                            <div className="col-8">{t('braingame.rightAnswer','Richtige Antwort')}: {correctAnswer}</div>
                        </div>
                    ) : null
                }

            </>
        )
    }

    const updateEntryModel = async () => {

        if (typeof activeEntry === 'undefined') {
            return;
        }

        if (typeof brainGameData === 'undefined') {
            return;
        }

        let newLevel = 0;

        if (checkSet === 'success') {
            newLevel = activeEntry.level;
            newLevel++;
            if (newLevel > 3) {
                newLevel = 3;
            }
        }

        activeEntry.level = newLevel;

        const api = new BrainGameApi(sessionToken, sessionEmail);

        const response = await api.updateKeyEntry(brainGameData.refId, activeEntry);

        if (typeof response === 'undefined') {
            console.error('response is undefined', response);
            return;
        }

        if (response.code >= 400) {
            return;
        }

        const newGameData: brainGame = response.data;
        dispatch(actions.brainGame.setData(newGameData));
    };

    useEffect(() => {
        if (typeof activeEntry === 'undefined') {
            getRandomEntry();
        }
    }, [brainGameData, selectedLanguage]);


    useEffect(()=>{
        if (checkSet === '') {
            return;
        }
        updateEntryModel();
    },[checkSet]);

    useEffect(() => {
        if (typeof activeEntry === 'undefined') {
            return;
        }

        randomQuestion();
    }, [activeEntry]);


    return (
        <>
            {typeof selectedLanguage === 'undefined' || typeof activeEntry === 'undefined' ? null : (
                <div className="row mb-3 align-items-center">
                    <div className="auto">
                        {showQuestion()}
                        {entryAnswer()}
                    </div>
                </div>
            )}
        </>
    );
}

export { GameInput };
